import { CircularProgress, Popover } from '@mui/material';
import { FC, useEffect, useRef } from 'react';

import { useNotifications } from './hooks/useNotifications';
import { EmptyContainer, NotificationsContainer } from './notifications.styles';
import { INotificationsMenu } from './notifications.types';
import NotificationsList from './NotificationsList';
import RefreshNotificationsButton from './RefreshNotificationsButton';

const NotificationsLoading = () => (
  <EmptyContainer>
    <CircularProgress size={24} />
  </EmptyContainer>
);

const NotificationsEmpty = () => (
  <EmptyContainer>No Notifications</EmptyContainer>
);

const NotificationsMenu: FC<INotificationsMenu> = ({
  anchorEl,
  isOpen,
  onClose,
  unreadCount,
}) => {
  const countAtPanelOpen = useRef<number | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const {
    handleRefreshNotifications,
    isLoadingNotifications,
    notifications,
    ...notificationsListProps
  } = useNotifications({ areNotificationsOpen: isOpen });

  useEffect(() => {
    if (!isOpen) {
      countAtPanelOpen.current = null;
      return;
    }
    if (countAtPanelOpen.current === null && unreadCount !== undefined) {
      countAtPanelOpen.current = unreadCount;
    }
  }, [isOpen, unreadCount]);

  const newNotificationsCount =
    isOpen && countAtPanelOpen.current !== null
      ? Math.max(0, (unreadCount ?? 0) - countAtPanelOpen.current)
      : 0;

  const handleRefreshNotificationsClick = () => {
    listRef.current?.scrollTo({ top: 0 });

    if (unreadCount !== undefined) {
      countAtPanelOpen.current = unreadCount;
    }

    handleRefreshNotifications();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      slotProps={{ paper: { sx: { backgroundColor: 'transparent' } } }}
    >
      {isLoadingNotifications ? (
        <NotificationsLoading />
      ) : !notifications?.length ? (
        <NotificationsEmpty />
      ) : (
        <NotificationsContainer>
          <NotificationsList
            {...notificationsListProps}
            notifications={notifications}
            listRef={listRef}
            onClose={onClose}
          />
          {newNotificationsCount > 0 && (
            <RefreshNotificationsButton
              newNotificationsCount={newNotificationsCount}
              onClick={handleRefreshNotificationsClick}
            />
          )}
        </NotificationsContainer>
      )}
    </Popover>
  );
};

export default NotificationsMenu;
